import { cosmeticItemSchema, type CosmeticItem, type CosmeticItemInput, type EquipmentSlot } from "@nano/contracts";

/**
 * Cosmetic shop catalog. The Worker prices purchases from this exact list
 * (never from the client), so any price change ships as a new
 * `COSMETIC_CATALOG_VERSION`.
 */
export const COSMETIC_CATALOG_VERSION = "catalog-1.1.0";

type Rarity = CosmeticItem["rarity"];

const item = (
  id: string,
  name: string,
  slot: EquipmentSlot,
  rarity: Rarity,
  priceGold: number,
  extra: Partial<CosmeticItemInput> = {},
): CosmeticItemInput => ({
  id,
  name,
  slot,
  rarity,
  priceGold,
  assetIds: { front: `cosmetic.${id}` },
  allowedArchetypes: "all",
  anchorsByPose: {},
  collection: "magical-toy-1",
  isAvailable: true,
  starter: false,
  hiddenInPoses: [],
  ...extra,
});

const faceHidden = { hiddenInPoses: ["back" as const] };
/** Headwear with its own profile and back art, so it turns with the head. */
const turned = (id: string) => ({ assetIds: { front: `cosmetic.${id}`, side: `cosmetic.${id}.side`, back: `cosmetic.${id}.back` } });

const CATALOG_INPUT: readonly CosmeticItemInput[] = [
  // Starter headwear: free for everyone, matches each archetype's look.
  item("hat-wizard", "Mũ phù thủy", "head", "common", 0, { starter: true, collection: "starter", ...turned("hat-wizard") }),
  item("tiara-pink", "Vương miện hồng", "head", "common", 0, { starter: true, collection: "starter", ...turned("tiara-pink") }),
  item("crown-gold", "Vương miện vàng", "head", "common", 0, { starter: true, collection: "starter", ...turned("crown-gold") }),
  item("hat-sorceress", "Mũ pháp sư hồng", "head", "common", 0, { starter: true, collection: "starter", ...turned("hat-sorceress") }),
  item("halo-gold", "Vòng hào quang", "head", "common", 0, { starter: true, collection: "starter" }),
  item("horns-imp", "Bờm sừng tinh nghịch", "head", "common", 0, { starter: true, collection: "starter", ...turned("horns-imp") }),

  item("crown-roses", "Vòng hoa hồng", "head", "rare", 300, turned("crown-roses")),
  item("crown-star", "Vương miện sao", "head", "epic", 800, turned("crown-star")),
  item("bunny-ears", "Tai thỏ", "head", "common", 150, turned("bunny-ears")),
  item("hat-witch", "Mũ phù thủy tím", "head", "rare", 350, turned("hat-witch")),
  item("hat-party", "Mũ tiệc", "head", "common", 120, turned("hat-party")),
  item("bow-pink", "Nơ hồng", "hair_accessory", "common", 100),
  item("clip-star", "Kẹp sao", "hair_accessory", "common", 120),
  item("clip-daisy", "Kẹp hoa cúc", "hair_accessory", "common", 110),
  item("clip-butterfly", "Kẹp bướm", "hair_accessory", "rare", 260),

  item("glasses-round", "Kính tròn", "face", "common", 140, faceHidden),
  item("glasses-heart", "Kính trái tim", "face", "rare", 280, faceHidden),
  item("glasses-star", "Kính ngôi sao", "face", "rare", 320, faceHidden),
  item("mask-masquerade", "Mặt nạ dạ hội", "face", "epic", 650, faceHidden),
  item("necklace-pearl", "Vòng ngọc trai", "neck", "rare", 300, faceHidden),
  item("necklace-heart", "Dây chuyền tim", "neck", "common", 180, faceHidden),
  item("bowtie-red", "Nơ cổ đỏ", "neck", "common", 130, faceHidden),
  item("scarf-striped", "Khăn sọc", "neck", "common", 160),
  item("medal-star", "Huy chương sao", "neck", "rare", 450, faceHidden),
  item("lei-flower", "Vòng hoa", "neck", "common", 190),
  item("scarf-rainbow", "Khăn cầu vồng", "neck", "rare", 380),
  item("collar-bell", "Vòng chuông", "neck", "common", 150, faceHidden),

  item("cape-red", "Áo choàng đỏ", "back", "rare", 480),
  item("wings-fairy", "Cánh tiên", "back", "epic", 900),
  item("wings-angel", "Cánh thiên thần", "back", "epic", 1000),
  item("cape-star", "Áo choàng sao", "back", "rare", 450),
  item("wand-heart", "Đũa trái tim", "hand", "common", 200),
  item("staff-crystal", "Gậy pha lê", "hand", "rare", 420),
  item("lollipop-giant", "Kẹo mút khổng lồ", "hand", "common", 170),
  item("balloon-star", "Bóng bay sao", "hand", "common", 150),
  item("aura-sparkle", "Hào quang lấp lánh", "aura", "epic", 700),
  item("aura-hearts", "Hào quang trái tim", "aura", "epic", 750),
  item("aura-rainbow", "Hào quang cầu vồng", "aura", "epic", 1200),
  item("aura-snow", "Hào quang bông tuyết", "aura", "epic", 850),
];

export const COSMETIC_CATALOG: readonly CosmeticItem[] = CATALOG_INPUT.map((input) =>
  cosmeticItemSchema.parse(input),
);

export const COSMETIC_BY_ID: ReadonlyMap<string, CosmeticItem> = new Map(
  COSMETIC_CATALOG.map((cosmetic) => [cosmetic.id, cosmetic]),
);

export const STARTER_ITEM_IDS: readonly string[] = COSMETIC_CATALOG.filter((c) => c.starter).map((c) => c.id);

/** Shop tabs in display order (spec §7.3). */
export const SHOP_TABS: readonly { id: "all" | EquipmentSlot; label: string }[] = [
  { id: "all", label: "Tất cả" },
  { id: "head", label: "Mũ và vương miện" },
  { id: "hair_accessory", label: "Tóc" },
  { id: "face", label: "Kính" },
  { id: "neck", label: "Vòng cổ" },
  { id: "back", label: "Cánh và áo choàng" },
  { id: "hand", label: "Đạo cụ" },
  { id: "aura", label: "Hiệu ứng" },
];
